import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'

const BANNER_SLIDES = [
  {
    id: 'halal',
    title: 'Zabiha Halal Meat',
    subtitle: 'Premium halal meat, prepared with care for your family.',
    backgroundImage: 'https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=1200&q=80',
    ambassadorImage: '/mrsai-halal.png',
    alt: 'Mr. Sai - Zabiha Halal Meat',
  },
  {
    id: 'indian',
    title: 'Home Style Indian Food',
    subtitle: 'Authentic ingredients and ready-to-cook favorites.',
    backgroundImage: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=1200&q=80',
    ambassadorImage: '/mrsai-indian.png',
    alt: 'Mr. Sai - Home Style Indian Food',
  },
  {
    id: 'panipuri',
    title: 'Live Panipuri',
    subtitle: 'Fresh, crispy panipuri and chaat essentials.',
    backgroundImage: 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=1200&q=80',
    ambassadorImage: '/mrsai-panipuri.png',
    alt: 'Mr. Sai - Live Panipuri',
  },
  {
    id: 'jewelry',
    title: 'Ethnic Jewelry',
    subtitle: 'Traditional and contemporary ethnic jewelry.',
    backgroundImage: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=1200&q=80',
    ambassadorImage: '/mrsai-jewelry.png',
    alt: 'Mr. Sai - Ethnic Jewelry',
  },
  {
    id: 'veggies',
    title: 'Fresh Veggie & Fruits',
    subtitle: 'Farm-fresh vegetables and fruits every day.',
    backgroundImage: 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=1200&q=80',
    ambassadorImage: '/mrsai-veggies.png',
    alt: 'Mr. Sai - Fresh Veggies & Fruits',
  },
]

const ROTATE_INTERVAL_MS = 5000
const SLIDE_DURATION_MS = 600

export default function HeroBanner() {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const goTo = useCallback((index: number) => {
    if (index === activeIndex) return
    setIsTransitioning(true)
    setActiveIndex(index)
  }, [activeIndex])

  const next = useCallback(() => {
    setIsTransitioning(true)
    setActiveIndex((i) => (i + 1) % BANNER_SLIDES.length)
  }, [])

  useEffect(() => {
    const id = setInterval(next, ROTATE_INTERVAL_MS)
    return () => clearInterval(id)
  }, [next])

  useEffect(() => {
    if (!isTransitioning) return
    const t = setTimeout(() => setIsTransitioning(false), SLIDE_DURATION_MS)
    return () => clearTimeout(t)
  }, [isTransitioning, activeIndex])

  return (
    <section className="relative w-full overflow-hidden rounded-2xl lg:rounded-3xl shadow-xl" aria-label="Hero banner carousel">
      {/* Banner height: min 100–180px by breakpoint, max 50% of (viewport - header); header ≈ 7rem on lg */}
      <div className="relative min-h-[100px] sm:min-h-[140px] lg:min-h-[180px] max-h-[calc(50vh-3.5rem)] bg-gradient-to-br from-primary-600 via-primary-500 to-accent-400">
        {/* Sliding track: 5 slides side by side */}
        <div
          className="flex h-full transition-transform ease-out"
          style={{
            width: `${BANNER_SLIDES.length * 100}%`,
            transform: `translateX(-${(100 / BANNER_SLIDES.length) * activeIndex}%)`,
            transitionDuration: `${SLIDE_DURATION_MS}ms`,
          }}
        >
          {BANNER_SLIDES.map((slide) => (
            <div
              key={slide.id}
              className="relative flex-shrink-0 w-full min-h-[100px] sm:min-h-[140px] lg:min-h-[180px] max-h-[calc(50vh-3.5rem)] flex items-center justify-center"
              style={{ width: `${100 / BANNER_SLIDES.length}%` }}
            >
              {/* 80/20 split frames with stretch-fill images */}
              <div className="relative z-10 flex w-full min-h-[100px] sm:min-h-[140px] lg:min-h-[180px] max-h-[calc(50vh-3.5rem)]">
                {/* Left frame: 80% */}
                <div className="relative w-[80%] min-h-[100px] sm:min-h-[140px] lg:min-h-[180px] max-h-[calc(50vh-3.5rem)] overflow-hidden">
                  <img
                    src={slide.backgroundImage}
                    alt={slide.title}
                    className="absolute inset-0 h-full w-full object-fill"
                    loading="eager"
                    decoding="async"
                  />
                  <div
                    className="absolute inset-0 bg-gradient-to-r from-primary-900/85 via-primary-700/65 to-primary-600/40"
                    aria-hidden
                  />
                  <div className="relative z-10 flex h-full items-center px-4 sm:px-6 lg:px-10 py-4 sm:py-6 lg:py-8">
                    <div className="text-left max-w-2xl">
                      <h2 className="text-lg leading-snug sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold text-white mb-1.5 sm:mb-2">
                        {slide.title}
                      </h2>
                      <p className="text-sm leading-snug sm:text-base md:text-lg text-white/90 mb-3 sm:mb-4 md:mb-5">
                        {slide.subtitle}
                      </p>
                  <Link
                    to="/#products"
                        className="inline-flex items-center gap-2 bg-white text-primary-600 font-semibold px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all border border-orange-200"
                      >
                        Shop now
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Right frame: 20% */}
                <div className="w-[20%] min-h-[100px] sm:min-h-[140px] lg:min-h-[180px] max-h-[calc(50vh-3.5rem)] overflow-hidden">
                  <img
                    src={slide.ambassadorImage}
                    alt={slide.alt}
                    className="h-full w-full object-fill"
                    loading="eager"
                    decoding="async"
                    onError={(e) => {
                      const target = e.currentTarget
                      if (target.src !== '/ShreeSaiLogo.png') {
                        target.src = '/ShreeSaiLogo.png'
                        target.alt = 'Shree Sai Grocery'
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dot indicators */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2" aria-label="Banner slides">
        {BANNER_SLIDES.map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => goTo(index)}
            className="h-2 w-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-700"
            style={{
              backgroundColor: index === activeIndex ? 'white' : 'rgba(255,255,255,0.5)',
              transform: index === activeIndex ? 'scale(1.25)' : 'scale(1)',
            }}
            aria-label={`Go to slide ${index + 1}: ${BANNER_SLIDES[index].title}`}
            aria-current={index === activeIndex ? 'true' : undefined}
          />
        ))}
      </div>
    </section>
  )
}
